import {Button} from '@ui/buttons/button';
import {BrandSocialIcon, SOCIAL_NETWORKS, detectSocialNetwork} from '@app/ui/brand-social-icons';
import {Link} from 'react-router';
import type {BlockInstance} from '../appearance-data';
import {useSelectedLocale} from '@ui/i18n/selected-locale';
import {getBootstrapData} from '@ui/bootstrap-data/bootstrap-data-store';
import {useChangeLocale} from '@common/locale-switcher/change-locale';

type P = {block: BlockInstance; props: Record<string, any>};
const arr = (v: any): any[] => (Array.isArray(v) ? v : []);
const alignClass = (a: string) =>
  a === 'center' ? 'items-center text-center' : a === 'end' ? 'items-end text-end' : 'items-start text-start';


/** Social links — a row of brand-coloured icon buttons. Great in footer/sidebar. */
export function SocialLinksBlock({props}: P) {
  const items = arr(props.items).filter(i => i?.url);
  if (!items.length) return null;
  return (
    <div className={`flex flex-col gap-12 px-16 py-20 ${alignClass(props.align)}`}>
      {props.heading ? <h3 className="text-sm font-semibold text-muted">{props.heading}</h3> : null}
      <div className="flex flex-wrap gap-12">
        {/* Real brand logos, big enough to tap (see brand-social-icons). The
            network comes from the block setting, or from the URL when unset. */}
        {items.map((item, i) => {
          const network =
            (SOCIAL_NETWORKS[item.network] ? item.network : null) ||
            detectSocialNetwork(item.url) ||
            'facebook';
          return <BrandSocialIcon key={i} network={network} href={item.url} size={48} />;
        })}
      </div>
    </div>
  );
}

/** Login / Register CTA — a compact conversion card for sidebars/footers. */
export function LoginCtaBlock({props}: P) {
  return (
    <div
      className="flex flex-col gap-10 rounded-2xl px-20 py-24 text-center"
      style={{backgroundColor: props.bg_color || '#0b3d2e', color: props.text_color || '#fff'}}
    >
      {props.heading ? <h3 className="text-lg font-bold">{props.heading}</h3> : null}
      {props.text ? <p className="text-sm opacity-90">{props.text}</p> : null}
      <div className="mt-4 flex flex-wrap items-center justify-center gap-10">
        {props.register_label ? (
          <Button variant="flat" color="primary" className="rounded-full"
            elementType={Link} to={props.register_url || '/register'}>
            {props.register_label}
          </Button>
        ) : null}
        {props.login_label ? (
          <Button variant="outline" color="white" className="rounded-full"
            elementType={Link} to={props.login_url || '/login'}>
            {props.login_label}
          </Button>
        ) : null}
      </div>
    </div>
  );
}

/** Language switcher — toggles between the site's configured locales. */
export function LanguageSwitcherBlock({props}: P) {
  const {localeCode} = useSelectedLocale();
  const changeLocale = useChangeLocale();
  const list: Array<{language: string; name: string}> =
    (getBootstrapData().i18n?.locales ?? []).map((l: any) => ({
      language: l.language,
      name: l.name,
    }));

  if (list.length < 2) return null;
  const inline = props.style === 'inline';
  const switchTo = (lang: string) => {
    if (lang !== localeCode) changeLocale.mutate({locale: lang});
  };

  return (
    <div className="flex flex-col gap-8 px-16 py-16">
      {props.heading ? <h3 className="text-sm font-semibold text-muted">{props.heading}</h3> : null}
      <div className={inline ? 'flex flex-wrap gap-12 text-sm' : 'flex flex-wrap gap-8'}>
        {list.map(l => {
          const active = l.language === localeCode;
          if (inline) {
            return (
              <button
                key={l.language}
                type="button"
                disabled={changeLocale.isPending}
                onClick={() => switchTo(l.language)}
                className={`hover:underline ${active ? 'font-semibold text-primary' : 'text-muted'}`}
              >
                {l.name}
              </button>
            );
          }
          return (
            <Button
              key={l.language}
              size="xs"
              variant={active ? 'flat' : 'outline'}
              color={active ? 'primary' : undefined}
              className="rounded-full"
              disabled={changeLocale.isPending}
              onClick={() => switchTo(l.language)}
            >
              {l.name}
            </Button>
          );
        })}
      </div>
    </div>
  );
}
